// Enhanced Assignment Modal Component with Hour Allocation Controls
import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import {
  Search,
  Users,
  Sparkles,
  UserPlus,
  AlertCircle,
  CheckCircle2,
  Clock,

  Filter,
  X,
  Loader2,
  Calculator,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Employee, ResourceAllocation } from '@/types';
import type {
  AssignmentModalProps,
  AssignmentMode,
  AssignmentSelection,
  PersonRecommendation,
  PersonAvailability,
  AvailabilityStatus,
} from '@/types/assignment.types';
import {
  generateRecommendations,
  filterPeople,
  calculatePersonAvailability,
  getUniqueValues,
  getDepartmentFilterOptions,
  formatHours,
  getAvailabilityColor,
  getAvailabilityBadgeVariant,
  getSuitabilityColor,
  getSuitabilityLabel,
} from '@/lib/assignment-utils';

interface AssignmentModalComponentProps extends AssignmentModalProps {
  availablePeople: Employee[];
  existingAllocations: ResourceAllocation[];
}

interface PersonAllocation {
  personId: number;
  allocatedHours: number;
  maxAvailableHours: number;
}

export function AssignmentModalEnhanced({
  open,
  onOpenChange,
  roleName,
  requiredHours,
  requiredSkills = [],
  projectId,
  projectStartDate,
  projectEndDate,
  onConfirm,
  allowMultiple = false,
  availablePeople,
  existingAllocations,
}: AssignmentModalComponentProps) {
  const [mode, setMode] = useState<AssignmentMode>('recommended');
  const [selectedPeople, setSelectedPeople] = useState<Set<number>>(new Set());
  const [allocations, setAllocations] = useState<Map<number, PersonAllocation>>(new Map());
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [departmentFilter, setDepartmentFilter] = useState<string[]>([]);
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityStatus[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [isGeneratingRecommendations, setIsGeneratingRecommendations] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedPeople(new Set());
      setAllocations(new Map());
      setSearchQuery('');
      setRoleFilter([]);
      setDepartmentFilter([]);
      setAvailabilityFilter([]);
      setShowFilters(false);
      setMode('recommended');
    }
  }, [open]);

  // Calculate availability for all people
  const availabilityMap = useMemo(() => {
    const map = new Map<number, PersonAvailability>();
    availablePeople.forEach(person => {
      const availability = calculatePersonAvailability(
        person,
        existingAllocations,
        projectStartDate,
        projectEndDate,
        projectId
      );
      map.set(person.id, availability);
    });
    return map;
  }, [availablePeople, existingAllocations, projectStartDate, projectEndDate, projectId]);

  // Generate recommendations
  const recommendations = useMemo(() => {
    if (mode !== 'recommended') return [];
    
    return generateRecommendations(
      availablePeople,
      existingAllocations,
      roleName,
      requiredHours,
      requiredSkills,
      projectId,
      projectStartDate,
      projectEndDate
    );
  }, [
    mode,
    availablePeople,
    existingAllocations,
    roleName,
    requiredHours,
    requiredSkills,
    projectId,
    projectStartDate,
    projectEndDate,
  ]);

  // Brief loading indicator when switching to recommended mode
  useEffect(() => {
    if (mode === 'recommended' && open) {
      setIsGeneratingRecommendations(true);
      const timer = setTimeout(() => setIsGeneratingRecommendations(false), 300);
      return () => clearTimeout(timer);
    }
  }, [mode, open]);

  // Filter people for manual mode
  const filteredPeople = useMemo(() => {
    if (mode !== 'manual') return [];
    
    // Always filter by the roleName to show only matching roles
    const rolesToFilter = roleFilter.length > 0 ? roleFilter : [roleName];
    
    return filterPeople(
      availablePeople,
      searchQuery,
      rolesToFilter,
      departmentFilter,
      availabilityFilter,
      availabilityMap
    );
  }, [mode, availablePeople, searchQuery, roleFilter, departmentFilter, availabilityFilter, availabilityMap, roleName]);

  // Get unique roles and departments for filters
  const uniqueRoles = useMemo(() => getUniqueValues(availablePeople.map(p => p.role)).filter(role => role && role.trim() !== '').sort(), [availablePeople]);
  const departmentOptions = useMemo(() => getDepartmentFilterOptions(availablePeople), [availablePeople]);

  // Calculate total allocated hours across all selections
  const totalAllocatedHours = useMemo(() => {
    let total = 0;
    allocations.forEach(allocation => {
      total += allocation.allocatedHours;
    });
    return total;
  }, [allocations]);

  // Calculate remaining hours needed
  const remainingHoursNeeded = Math.max(0, requiredHours - totalAllocatedHours);

  // Handle person selection
  const togglePersonSelection = (personId: number) => {
    const newSelection = new Set(selectedPeople);
    const newAllocations = new Map(allocations);
    
    if (newSelection.has(personId)) {
      // Deselect person
      newSelection.delete(personId);
      newAllocations.delete(personId);
    } else {
      // Select person
      if (!allowMultiple) {
        newSelection.clear();
        newAllocations.clear();
      }
      newSelection.add(personId);
      
      // Initialize allocation with suggested hours
      const availability = availabilityMap.get(personId);
      const maxAvailable = availability?.availableHours || 0;
      const suggestedHours = Math.min(remainingHoursNeeded || requiredHours, maxAvailable);
      
      newAllocations.set(personId, {
        personId,
        allocatedHours: suggestedHours,
        maxAvailableHours: maxAvailable,
      });
    }
    
    setSelectedPeople(newSelection);
    setAllocations(newAllocations);
  };

  // Update allocation hours for a person
  const updateAllocation = (personId: number, hours: number) => {
    const newAllocations = new Map(allocations);
    const allocation = newAllocations.get(personId);
    if (allocation) {
      allocation.allocatedHours = Math.max(0, Math.min(hours, allocation.maxAvailableHours));
      newAllocations.set(personId, allocation);
      setAllocations(newAllocations);
    }
  };

  // Handle confirm
  const handleConfirm = () => {
    const selections: AssignmentSelection[] = Array.from(selectedPeople).map(personId => {
      const person = availablePeople.find(p => p.id === personId);
      const allocation = allocations.get(personId);
      const availability = availabilityMap.get(personId);
      
      return {
        personId,
        personName: person?.name || '',
        role: person?.role || '',
        allocatedHours: allocation?.allocatedHours || 0,
        percentageOfCapacity: availability?.totalCapacity 
          ? Math.round((allocation?.allocatedHours || 0) / availability.totalCapacity * 100)
          : 0,
      };
    });
    
    onConfirm(selections);
    onOpenChange(false);
  };

  // Handle cancel
  const handleCancel = () => {
    onOpenChange(false);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setRoleFilter([]);
    setDepartmentFilter([]);
    setAvailabilityFilter([]);
  };

  const hasActiveFilters = searchQuery || roleFilter.length > 0 || departmentFilter.length > 0 || availabilityFilter.length > 0;
  const hasValidAllocations = selectedPeople.size > 0 && Array.from(allocations.values()).every(a => a.allocatedHours > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0 overflow-hidden" hideClose={false} allowOutsideClick={false}>
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
          <DialogTitle className="text-2xl flex items-center gap-2">
            <UserPlus className="h-6 w-6" />
            Assign Team Member with Hour Allocation
          </DialogTitle>
          <DialogDescription asChild className="text-base mt-2">
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span>Assigning to role:</span>
                <Badge variant="outline" className="text-base font-semibold">
                  {roleName}
                </Badge>
                <Separator orientation="vertical" className="h-4" />
                <span className="text-muted-foreground">
                  {formatHours(requiredHours)} needed
                </span>
                {requiredSkills.length > 0 && (
                  <>
                    <Separator orientation="vertical" className="h-4" />
                    <span className="text-muted-foreground">
                      {requiredSkills.length} skill{requiredSkills.length !== 1 ? 's' : ''} required
                    </span>
                  </>
                )}
              </div>
              
              {/* Allocation Summary */}
              {selectedPeople.size > 0 && (
                <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                  <Calculator className="h-5 w-5 text-primary" />
                  <div className="flex items-center gap-4 text-sm flex-wrap">
                    <div>
                      <span className="text-muted-foreground">Total Allocated:</span>
                      <span className="ml-2 font-semibold text-primary">{formatHours(totalAllocatedHours)}</span>
                    </div>
                    <Separator orientation="vertical" className="h-4" />
                    <div>
                      <span className="text-muted-foreground">Remaining Needed:</span>
                      <span className={cn(
                        "ml-2 font-semibold",
                        remainingHoursNeeded === 0 ? "text-success-600" : "text-warning-600"
                      )}>
                        {formatHours(remainingHoursNeeded)}
                      </span>
                    </div>
                    <Separator orientation="vertical" className="h-4" />
                    <div>
                      <span className="text-muted-foreground">Coverage:</span>
                      <span className="ml-2 font-semibold">
                        {Math.round((totalAllocatedHours / requiredHours) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Mode Tabs */}
        <div className="flex-1 flex flex-col overflow-hidden px-6">
          <Tabs value={mode} onValueChange={(value) => setMode(value as AssignmentMode)} className="w-full flex flex-col flex-1 overflow-hidden">
            <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
              <TabsTrigger value="recommended" className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Recommended
              </TabsTrigger>
              <TabsTrigger value="manual" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Manual Selection
              </TabsTrigger>
            </TabsList>

            {/* Recommended Mode */}
            <TabsContent value="recommended" className="mt-4 space-y-4 flex-1 overflow-y-auto">
              {/* Loading State */}
              {isGeneratingRecommendations && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center space-y-3">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                    <p className="text-sm text-muted-foreground">Analysing team members...</p>
                  </div>
                </div>
              )}

              {/* Recommendations List */}
              {!isGeneratingRecommendations && (
                <>
                  {recommendations.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                      <p className="text-muted-foreground">No team members available</p>
                    </div>
                  ) : (
                    <div className="space-y-3 pr-4">
                      {recommendations.map((rec) => {
                        if (!rec.availability) return null;
                        return (
                          <RecommendationCardEnhanced
                            key={rec.person.id}
                            recommendation={rec}
                            isSelected={selectedPeople.has(rec.person.id)}
                            onSelect={() => togglePersonSelection(rec.person.id)}
                            allocation={allocations.get(rec.person.id)}
                            onUpdateAllocation={(hours) => updateAllocation(rec.person.id, hours)}
                            requiredHours={requiredHours}
                            remainingHoursNeeded={remainingHoursNeeded}
                          />
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* Manual Mode */}
            <TabsContent value="manual" className="mt-4 space-y-4 flex-1 overflow-y-auto">
              {/* Search and Filters */}
              <div className="space-y-3">
                {/* Search Bar */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, role, email, or skills..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-9"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {/* Filter Toggle */}
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className="gap-2"
                  >
                    <Filter className="h-4 w-4" />
                    Filters
                    {hasActiveFilters && (
                      <Badge variant="secondary" className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                        {[roleFilter.length, departmentFilter.length, availabilityFilter.length].reduce((a, b) => a + b, 0)}
                      </Badge>
                    )}
                  </Button>
                  {hasActiveFilters && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      Clear all
                    </Button>
                  )}
                </div>

                {/* Filter Panel */}
                {showFilters && (
                  <Card>
                    <CardContent className="pt-4 space-y-4">
                      {/* Role Filter */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Role</Label>
                        <Select
                          value={roleFilter[0] || '__all__'}
                          onValueChange={(value) => setRoleFilter(value === '__all__' ? [] : [value])}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All roles" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">All roles</SelectItem>
                            {uniqueRoles.map(role => (
                              <SelectItem key={role} value={role}>{role}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Department Filter */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Department</Label>
                        <Select
                          value={departmentFilter[0] || '__all__'}
                          onValueChange={(value) => setDepartmentFilter(value === '__all__' ? [] : [value])}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All departments" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">All departments</SelectItem>
                            {departmentOptions.map(dept => (
                              <SelectItem key={dept.value} value={dept.value}>{dept.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Availability Filter */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Availability</Label>
                        <div className="space-y-2">
                          {(['available', 'partially-available', 'unavailable'] as AvailabilityStatus[]).map(status => (
                            <div key={status} className="flex items-center space-x-2">
                              <Checkbox
                                id={`availability-${status}`}
                                checked={availabilityFilter.includes(status)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setAvailabilityFilter([...availabilityFilter, status]);
                                  } else {
                                    setAvailabilityFilter(availabilityFilter.filter(s => s !== status));
                                  }
                                }}
                              />
                              <Label htmlFor={`availability-${status}`} className="text-sm font-normal cursor-pointer">
                                {status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* People List */}
              <div className="pr-4">
                {filteredPeople.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-muted-foreground">
                      {hasActiveFilters ? 'No team members match your filters' : 'No team members available'}
                    </p>
                    {hasActiveFilters && (
                      <Button variant="link" onClick={clearFilters} className="mt-2">
                        Clear filters
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredPeople.map((person) => {
                      const availability = availabilityMap.get(person.id);
                      if (!availability) return null;
                      return (
                        <PersonCardEnhanced
                          key={person.id}
                          person={person}
                          availability={availability}
                          isSelected={selectedPeople.has(person.id)}
                          onSelect={() => togglePersonSelection(person.id)}
                          allocation={allocations.get(person.id)}
                          onUpdateAllocation={(hours) => updateAllocation(person.id, hours)}
                          requiredHours={requiredHours}
                          remainingHoursNeeded={remainingHoursNeeded}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t flex-shrink-0">
          <div className="flex items-center justify-between w-full">
            <div className="text-sm">
              {selectedPeople.size > 0 ? (
                <div className="space-y-1">
                  <p className="font-medium">
                    {selectedPeople.size} {selectedPeople.size === 1 ? 'person' : 'people'} selected
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {formatHours(totalAllocatedHours)} of {formatHours(requiredHours)} allocated
                  </p>
                </div>
              ) : (
                <span className="text-muted-foreground">No selection</span>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleConfirm} disabled={!hasValidAllocations}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Confirm Assignment
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Enhanced Recommendation Card Component with Hour Allocation
function RecommendationCardEnhanced({
  recommendation,
  isSelected,
  onSelect,
  allocation,
  onUpdateAllocation,
  requiredHours,
  remainingHoursNeeded,
}: {
  recommendation: PersonRecommendation;
  isSelected: boolean;
  onSelect: () => void;
  allocation?: PersonAllocation;
  onUpdateAllocation: (hours: number) => void;
  requiredHours: number;
  remainingHoursNeeded: number;
}) {
  const { person, suitabilityScore, skillsMatchPercent, rationale, warnings } = recommendation;
  const skills = person.skills ?? [];
  // Safely extract availability with fallback to prevent crashes
  const availability = recommendation.availability;
  
  // Check availability validity BEFORE any hooks that might depend on it
  const hasValidAvailability = availability && 
    typeof availability.status === 'string' && 
    typeof availability.availableHours === 'number' &&
    typeof availability.totalCapacity === 'number';
  
  const [inputValue, setInputValue] = useState(allocation?.allocatedHours.toString() || '');

  useEffect(() => {
    if (allocation) {
      setInputValue(allocation.allocatedHours.toString());
    }
  }, [allocation]);

  const handleHourChange = (value: string) => {
    setInputValue(value);
    const hours = parseFloat(value) || 0;
    onUpdateAllocation(hours);
  };

  const handleSliderChange = (value: number[]) => {
    const hours = value[0];
    setInputValue(hours.toString());
    onUpdateAllocation(hours);
  };

  // Early return if availability is not valid
  if (!hasValidAvailability) {
    console.warn('RecommendationCardEnhanced: Missing or invalid availability data for person', person?.id, availability);
    return null;
  }

  const percentageOfCapacity = allocation 
    ? Math.round((allocation.allocatedHours / availability.totalCapacity) * 100)
    : 0;

  const remainingAfterAllocation = allocation
    ? availability.availableHours - allocation.allocatedHours
    : availability.availableHours;

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:shadow-md',
        isSelected && 'ring-2 ring-primary'
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Selection Checkbox */}
          <div className="pt-1">
            <Checkbox checked={isSelected} onCheckedChange={onSelect} />
          </div>

          {/* Person Info */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-base">{person.name}</h4>
                  <Badge variant="outline" className="text-xs">
                    #{recommendation.rank}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{person.role}</p>
                <p className="text-xs text-muted-foreground">{person.department}</p>
              </div>

              {/* Suitability Score */}
              <div className="text-right shrink-0">
                <div className={cn('text-2xl font-bold', getSuitabilityColor(suitabilityScore))}>
                  {suitabilityScore}%
                </div>
                <p className="text-xs text-muted-foreground">{getSuitabilityLabel(suitabilityScore)}</p>
              </div>
            </div>

            {/* Metrics */}
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Available:</span>
                <span className="font-medium">{formatHours(availability.availableHours)}</span>
              </div>
              <Separator orientation="vertical" className="h-4" />
              <Badge variant={getAvailabilityBadgeVariant(availability.status)}>
                {availability.status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </Badge>
            </div>

            {/* Hour Allocation Controls - Only show when selected */}
            {isSelected && allocation && (
              <div className="p-4 bg-primary/5 rounded-lg space-y-4 border border-primary/20">
                <div className="flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-primary" />
                  <h5 className="font-semibold text-sm">Hour Allocation</h5>
                </div>

                {/* Allocation Input and Slider */}
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Label htmlFor={`hours-${person.id}`} className="text-xs text-muted-foreground mb-1.5 block">
                        Allocated Hours
                      </Label>
                      <Input
                        id={`hours-${person.id}`}
                        type="number"
                        min="0"
                        max={allocation.maxAvailableHours}
                        step="1"
                        value={inputValue}
                        onChange={(e) => handleHourChange(e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground mb-1.5">% of Capacity</p>
                      <Badge variant="secondary" className="text-sm font-semibold">
                        {percentageOfCapacity}%
                      </Badge>
                    </div>
                  </div>

                  {/* Slider */}
                  <div className="space-y-1.5">
                    <Slider
                      value={[allocation.allocatedHours]}
                      onValueChange={handleSliderChange}
                      max={allocation.maxAvailableHours}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0h</span>
                      <span>{formatHours(allocation.maxAvailableHours)} max</span>
                    </div>
                  </div>
                </div>

                {/* Allocation Calculation Display */}
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="text-center p-2 bg-background rounded border">
                    <p className="text-xs text-muted-foreground mb-1">Total Available</p>
                    <p className="font-semibold">{formatHours(availability.availableHours)}</p>
                  </div>
                  <div className="text-center p-2 bg-background rounded border">
                    <p className="text-xs text-muted-foreground mb-1">Allocated</p>
                    <p className="font-semibold text-primary">{formatHours(allocation.allocatedHours)}</p>
                  </div>
                  <div className="text-center p-2 bg-background rounded border">
                    <p className="text-xs text-muted-foreground mb-1">Remaining</p>
                    <p className={cn(
                      "font-semibold",
                      remainingAfterAllocation < 0 ? "text-error-600" : "text-success-600"
                    )}>
                      {formatHours(remainingAfterAllocation)}
                    </p>
                  </div>
                </div>

                {/* Validation Messages */}
                {allocation.allocatedHours > allocation.maxAvailableHours && (
                  <div className="flex items-start gap-2 text-sm text-error-600">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <p>Allocation exceeds available hours</p>
                  </div>
                )}
                {allocation.allocatedHours === 0 && (
                  <div className="flex items-start gap-2 text-sm text-warning-600">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <p>Please specify hours to allocate</p>
                  </div>
                )}
              </div>
            )}

            {/* Rationale */}
            {rationale.length > 0 && (
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-success-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-muted-foreground">
                    {rationale.join(' • ')}
                  </p>
                </div>
              </div>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-warning-600 mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-warning-600">
                    {warnings.join(' • ')}
                  </p>
                </div>
              </div>
            )}

            {/* Skills */}
            {skills.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {skills.slice(0, 5).map(skill => (
                  <Badge key={skill} variant="secondary" className="text-xs">
                    {skill}
                  </Badge>
                ))}
                {skills.length > 5 && (
                  <span className="text-xs text-muted-foreground">
                    +{skills.length - 5} more
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Enhanced Person Card Component (Manual Mode) with Hour Allocation
function PersonCardEnhanced({
  person,
  availability,
  isSelected,
  onSelect,
  allocation,
  onUpdateAllocation,
  requiredHours,
  remainingHoursNeeded,
}: {
  person: Employee;
  availability: PersonAvailability;
  isSelected: boolean;
  onSelect: () => void;
  allocation?: PersonAllocation;
  onUpdateAllocation: (hours: number) => void;
  requiredHours: number;
  remainingHoursNeeded: number;
}) {
  // Check availability validity BEFORE any hooks that might depend on it
  const hasValidAvailability = availability && 
    typeof availability.status === 'string' && 
    typeof availability.availableHours === 'number' &&
    typeof availability.totalCapacity === 'number';
  
  const [inputValue, setInputValue] = useState(allocation?.allocatedHours.toString() || '');

  useEffect(() => {
    if (allocation) {
      setInputValue(allocation.allocatedHours.toString());
    }
  }, [allocation]);

  const handleHourChange = (value: string) => {
    setInputValue(value);
    const hours = parseFloat(value) || 0;
    onUpdateAllocation(hours);
  };

  const handleSliderChange = (value: number[]) => {
    const hours = value[0];
    setInputValue(hours.toString());
    onUpdateAllocation(hours);
  };

  // Early return if availability is not valid
  if (!hasValidAvailability) {
    console.warn('PersonCardEnhanced: Missing or invalid availability data for person', person?.id, availability);
    return null;
  }

  const percentageOfCapacity = allocation 
    ? Math.round((allocation.allocatedHours / availability.totalCapacity) * 100)
    : 0;

  const remainingAfterAllocation = allocation
    ? availability.availableHours - allocation.allocatedHours
    : availability.availableHours;

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:shadow-md',
        isSelected && 'ring-2 ring-primary'
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Selection Checkbox */}
          <div className="pt-1">
            <Checkbox checked={isSelected} onCheckedChange={onSelect} />
          </div>

          {/* Person Info */}
          <div className="flex-1 min-w-0 space-y-3">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-base">{person.name}</h4>
                <p className="text-sm text-muted-foreground">{person.role}</p>
                <p className="text-xs text-muted-foreground">{person.department}</p>
              </div>

              {/* Availability Badge */}
              <Badge variant={getAvailabilityBadgeVariant(availability.status)}>
                {availability.status.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </Badge>
            </div>

            {/* Availability Info */}
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Available:</span>
                <span className={cn('font-medium', getAvailabilityColor(availability.status))}>
                  {formatHours(availability.availableHours)}
                </span>
              </div>
              <Separator orientation="vertical" className="h-4" />
              <div className="text-muted-foreground">
                Utilisation: <span className="font-medium">{availability.utilisationPercent}%</span>
              </div>
            </div>

            {/* Hour Allocation Controls - Only show when selected */}
            {isSelected && allocation && (
              <div className="p-4 bg-primary/5 rounded-lg space-y-4 border border-primary/20">
                <div className="flex items-center gap-2">
                  <Calculator className="h-4 w-4 text-primary" />
                  <h5 className="font-semibold text-sm">Hour Allocation</h5>
                </div>

                {/* Allocation Input and Slider */}
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <Label htmlFor={`hours-${person.id}`} className="text-xs text-muted-foreground mb-1.5 block">
                        Allocated Hours
                      </Label>
                      <Input
                        id={`hours-${person.id}`}
                        type="number"
                        min="0"
                        max={allocation.maxAvailableHours}
                        step="1"
                        value={inputValue}
                        onChange={(e) => handleHourChange(e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground mb-1.5">% of Capacity</p>
                      <Badge variant="secondary" className="text-sm font-semibold">
                        {percentageOfCapacity}%
                      </Badge>
                    </div>
                  </div>

                  {/* Slider */}
                  <div className="space-y-1.5">
                    <Slider
                      value={[allocation.allocatedHours]}
                      onValueChange={handleSliderChange}
                      max={allocation.maxAvailableHours}
                      step={1}
                      className="w-full"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0h</span>
                      <span>{formatHours(allocation.maxAvailableHours)} max</span>
                    </div>
                  </div>
                </div>

                {/* Allocation Calculation Display */}
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="text-center p-2 bg-background rounded border">
                    <p className="text-xs text-muted-foreground mb-1">Total Available</p>
                    <p className="font-semibold">{formatHours(availability.availableHours)}</p>
                  </div>
                  <div className="text-center p-2 bg-background rounded border">
                    <p className="text-xs text-muted-foreground mb-1">Allocated</p>
                    <p className="font-semibold text-primary">{formatHours(allocation.allocatedHours)}</p>
                  </div>
                  <div className="text-center p-2 bg-background rounded border">
                    <p className="text-xs text-muted-foreground mb-1">Remaining</p>
                    <p className={cn(
                      "font-semibold",
                      remainingAfterAllocation < 0 ? "text-error-600" : "text-success-600"
                    )}>
                      {formatHours(remainingAfterAllocation)}
                    </p>
                  </div>
                </div>

                {/* Validation Messages */}
                {allocation.allocatedHours > allocation.maxAvailableHours && (
                  <div className="flex items-start gap-2 text-sm text-error-600">
                    <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    <p>Allocation exceeds available hours</p>
                  </div>
                )}
                {allocation.allocatedHours === 0 && (
                  <div className="flex items-start gap-2 text-sm text-warning-600">
                    <Info className="h-4 w-4 mt-0.5 shrink-0" />
                    <p>Please specify hours to allocate</p>
                  </div>
                )}
              </div>
            )}

            {/* Conflicts Warning */}
            {availability.conflicts.length > 0 && (
              <div className="flex items-start gap-2">
                <AlertCircle className="h-4 w-4 text-warning-600 mt-0.5 shrink-0" />
                <p className="text-sm text-warning-600">
                  {availability.conflicts.length} project conflict{availability.conflicts.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}

            {/* Skills */}
            {(person.skills ?? []).length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                {(person.skills ?? []).slice(0, 5).map(skill => (
                  <Badge key={skill} variant="secondary" className="text-xs">
                    {skill}
                  </Badge>
                ))}
                {(person.skills ?? []).length > 5 && (
                  <span className="text-xs text-muted-foreground">
                    +{(person.skills ?? []).length - 5} more
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
